import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Info, ShieldCheck } from 'lucide-react';
import { Card } from '../Card';
import { Button } from '../Button';
import * as api from '../../../services/api';
import { useToast } from '../../../contexts/ToastContext';

/**
 * CommandLimitsTab — Admin Center "Command Limits".
 *
 * The ceiling on a single governed command's argument payload, per customer. A connector action
 * whose arguments legitimately run large needs this raised; a tighter blast radius needs it
 * lowered. Neither should require a platform ticket, so a customer's own Super Admin sets it here.
 *
 * Reads /me/entitlements, which resolves the customer server-side and publishes the whole envelope
 * (effective, default, min, max). Nothing about the limit is stated in this file: a number written
 * here would be a second source of truth that drifts from the enforced one the first time deploy
 * config changes.
 *
 * Writes through the existing per-customer quota endpoint — the same store, audit row and scope
 * rule as every other customer limit. The write is keyed on the customer id the SERVER resolved,
 * not one inferred from the browser's token.
 */
export default function CommandLimitsTab() {
  const { addToast } = useToast();

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [draftKb, setDraftKb] = useState('');

  const limits = summary?.commandLimits || null;
  const customerId = summary?.customerId || null;

  const toKb = (bytes) => (bytes == null ? null : Math.round(bytes / 1024));

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.getMyEntitlements();
      if (res?.ok) {
        setSummary(res.data);
        const cl = res.data?.commandLimits;
        // Seed the field with what is CONFIGURED, not what is effective: an empty field means
        // "not set, using the platform default", and pre-filling it with the default would make
        // every unconfigured customer look as though someone had chosen that number.
        setDraftKb(cl?.argumentBytesConfigured != null ? String(toKb(cl.argumentBytesConfigured)) : '');
      } else {
        setError(res?.error || 'This setting could not be loaded.');
      }
    } catch (e) {
      setError(e?.message || 'This setting could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const minKb = toKb(limits?.argumentBytesMin) ?? 1;
  const maxKb = toKb(limits?.argumentBytesMax) ?? null;
  const defaultKb = toKb(limits?.argumentBytesDefault);
  const effectiveKb = toKb(limits?.argumentBytesEffective);
  const isCustomerSet = limits?.argumentBytesConfigured != null;

  /** Client-side guidance only — the platform is authoritative and refuses out-of-range values. */
  const draftProblem = useMemo(() => {
    const raw = String(draftKb).trim();
    if (raw === '') return null;                       // empty is valid: it means "use the default"
    if (!/^\d+$/.test(raw)) return 'Enter a whole number of kilobytes.';
    const kb = parseInt(raw, 10);
    if (kb < minKb) return `The smallest allowed size is ${minKb} KB.`;
    if (maxKb != null && kb > maxKb) return `The largest size allowed on this platform is ${maxKb} KB.`;
    return null;
  }, [draftKb, minKb, maxKb]);

  const save = async (reset) => {
    if (!customerId) return;
    if (!reset && draftProblem) return;
    setSaving(true);
    try {
      const hard = reset ? null : (String(draftKb).trim() === '' ? null : parseInt(draftKb, 10) * 1024);
      const res = await api.setQuota(customerId, 'COMMAND_ARGUMENT_BYTES', null, hard);
      if (res?.ok === false) throw new Error(res.error || 'This limit could not be updated.');
      addToast({
        tone: 'success',
        message: hard == null ? 'Argument limit reset to the platform default' : 'Argument limit updated',
      });
      await reload();
    } catch (e) {
      addToast({ tone: 'danger', message: e?.message || 'This limit could not be updated.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading && !summary) {
    return <div style={{ padding: '20px 28px' }} className="text-[12px] text-v2-ink-muted">Loading command limits…</div>;
  }
  if (error) {
    return (
      <div style={{ padding: '20px 28px' }}>
        <div className="text-[12px] text-v2-ink mb-2">{error}</div>
        <Button variant="secondary" size="sm" onClick={reload}>Try again</Button>
      </div>
    );
  }

  // A Platform Admin who has not switched into a customer has no customer to set this for. Say so
  // plainly rather than rendering a control whose save would have nowhere to land.
  if (!customerId || !limits) {
    return (
      <div style={{ padding: '20px 28px' }}>
        <Card padding="sm">
          <Card.Body>
            <div className="text-[12px] font-semibold text-v2-ink mb-1">No customer selected</div>
            <div className="text-[11px] text-v2-ink-muted">
              This limit is set per customer. Switch into a customer from the Customers section to
              set it for them.
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 28px 32px' }}>
      <Card padding="sm">
        <Card.Body>
          <div className="flex items-start gap-2 mb-2">
            <ShieldCheck size={14} className="mt-0.5 shrink-0 text-v2-ink-muted" />
            <div>
              <div className="text-[12px] font-semibold text-v2-ink">Command argument size</div>
              <div className="text-[11px] text-v2-ink-muted mt-0.5">
                The most a single protected action may carry in its arguments. A request above this
                size is declined before it reaches the connector, and the decline is recorded.
                Raise it for an action that legitimately sends large values; lower it to narrow what
                one request can carry.
              </div>
            </div>
          </div>

          <div className="flex items-end gap-3 mt-3">
            <label className="block">
              <span className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">
                Limit
              </span>
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  inputMode="numeric"
                  aria-label="Command argument size limit in kilobytes"
                  className="w-24 text-right rounded-md border border-v2-border px-2 py-1 text-[12px]"
                  value={draftKb}
                  placeholder={defaultKb != null ? String(defaultKb) : ''}
                  onChange={(e) => setDraftKb(e.target.value)}
                />
                <span className="text-[11px] text-v2-ink-muted">KB</span>
              </div>
            </label>

            <Button variant="primary" size="sm" onClick={() => save(false)}
                    disabled={saving || !!draftProblem}>
              Save limit
            </Button>
            {isCustomerSet && (
              <Button variant="secondary" size="sm" onClick={() => save(true)} disabled={saving}>
                Use platform default
              </Button>
            )}
          </div>

          {draftProblem && (
            <div className="text-[11px] text-v2-danger mt-2">{draftProblem}</div>
          )}

          <div className="mt-3 pt-3 border-t border-v2-border-subtle text-[11px] text-v2-ink-muted">
            <div>
              In effect now: <span className="font-semibold text-v2-ink">{effectiveKb} KB</span>
              {isCustomerSet ? ' — set for this customer' : ' — the platform default, because no limit is set here'}
            </div>
            <div className="mt-0.5">
              Allowed range {minKb} KB to {maxKb != null ? `${maxKb} KB` : 'the platform maximum'}
              {defaultKb != null && <> · platform default {defaultKb} KB</>}
            </div>
          </div>
        </Card.Body>
      </Card>

      <div className="mt-3 flex items-start gap-2 text-[11px] text-v2-ink-muted">
        <Info size={13} className="mt-0.5 shrink-0" />
        <div>
          Leaving the field empty and saving is the same as using the platform default: no limit is
          stored for this customer and the platform value applies, including if it changes later.
        </div>
      </div>
    </div>
  );
}
